import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ChevronRight } from 'lucide-react';
import { LoadingSpinner } from '../../components/ui/LoadingSpinner';
import { CalculateTaxesCard } from '../../components/ui/CalculateTaxesCard';
import { usePlatformQuoteDefaults } from '../../hooks/usePlatformQuoteDefaults';
import { Organization } from '../../types/admin';
import { OrgSettingsUpdate } from '../../hooks/useOrgSettings';
import {
  QuoteBuildingCard,
  ShiftSettingsCard,
  PricingModelCard,
  ItemizationCard,
} from './QuoteDefaultsPage';

export function PlatformQuoteDefaultsPage() {
  const navigate = useNavigate();
  const { defaults, loading, error, updateDefaults } = usePlatformQuoteDefaults();
  const [activeTab, setActiveTab] = useState<'commercial' | 'residential'>('commercial');

  async function handleSave(data: OrgSettingsUpdate) {
    await updateDefaults(data as Record<string, unknown>);
  }

  // Build an Organization-shaped object from platform defaults so card components work
  const org: Organization = {
    id: '__platform__',
    name: 'Platform Defaults',
    maxUsers: 0,
    createdAt: '',
    updatedAt: '',
    // Spread all platform defaults — they use the same field names as Organization
    ...defaults,
    // Always enable both types on the platform defaults page
    featureCommercialMovingQuotes: true,
    featureResidentialMovingQuotes: true,
  };

  const residentialPricingModel = org.residentialPricingModel ?? 'hourly_rate';

  return (
    <div className="px-4 pt-6 pb-12 md:px-8 md:max-w-2xl lg:max-w-4xl md:mx-auto">
      {/* Breadcrumb */}
      <nav className="flex items-center gap-1 text-small mb-4 -ml-1">
        <button
          type="button"
          onClick={() => navigate('/admin/platform-tools')}
          className="text-slate hover:text-midnight transition-colors duration-150"
        >
          Platform Admin Tools
        </button>
        <ChevronRight size={14} className="text-stone" />
        <button
          type="button"
          onClick={() => navigate('/admin/platform-tools/defaults')}
          className="text-slate hover:text-midnight transition-colors duration-150"
        >
          Platform Defaults
        </button>
        <ChevronRight size={14} className="text-stone" />
        <span className="text-midnight font-medium">Quote Defaults</span>
      </nav>

      <h1 className="font-display text-h1 text-midnight mb-1">Platform Quote Defaults</h1>
      <p className="text-[13px] text-slate mb-6">
        These are the default quote settings applied to every new organization. Org admins can customize their own settings and restore them from these platform defaults at any time.
      </p>

      {loading && (
        <div className="flex justify-center py-16">
          <LoadingSpinner size="lg" />
        </div>
      )}

      {!loading && error && (
        <div className="flex flex-col items-center py-16 gap-2 text-center">
          <p className="text-small text-rose font-semibold">Failed to load settings</p>
          <p className="text-small text-slate">{error}</p>
        </div>
      )}

      {!loading && !error && defaults && (
        <div className="flex flex-col gap-4">
          {/* Tabs */}
          <div className="flex bg-mist rounded-lg p-1 gap-1">
            {(['commercial', 'residential'] as const).map(tab => (
              <button
                key={tab}
                type="button"
                onClick={() => setActiveTab(tab)}
                className={`flex-1 py-2.5 text-sm font-semibold rounded-md transition-all duration-150
                  ${activeTab === tab
                    ? 'bg-white text-midnight shadow-brand-sm'
                    : 'text-slate hover:text-midnight'
                  }`}
              >
                {tab === 'commercial' ? 'Commercial' : 'Residential'}
              </button>
            ))}
          </div>

          {/* Commercial tab content */}
          {activeTab === 'commercial' && (
            <div className="flex flex-col gap-4">
              <QuoteBuildingCard type="commercial" org={org} onSave={handleSave} />
              <ShiftSettingsCard type="commercial" org={org} onSave={handleSave} />
              <CalculateTaxesCard org={org} onSave={handleSave} />
              <ItemizationCard type="commercial" org={org} onSave={handleSave} />
            </div>
          )}

          {/* Residential tab content */}
          {activeTab === 'residential' && (
            <div className="flex flex-col gap-4">
              <QuoteBuildingCard type="residential" org={org} onSave={handleSave} />
              <ShiftSettingsCard type="residential" org={org} onSave={handleSave} />
              <PricingModelCard org={org} onSave={handleSave} />
              <CalculateTaxesCard org={org} onSave={handleSave} />
              <ItemizationCard type="residential" org={org} onSave={handleSave} flatRateMode={residentialPricingModel === 'flat_rate'} />
            </div>
          )}
        </div>
      )}
    </div>
  );
}
